import React from "react";
import { Helmet } from "react-helmet-async";
import { ChevronDown, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const FaqMode = ({ setOpenIndex, openIndex, toggleFAQ, idCategorie }) => {
  const faqList = [
    {
      question: () => (
        <strong>
          1. Pourquoi utiliser Deal.mg pour acheter des articles de mode ?
        </strong>
      ),
      answer: () => (
        <p>
          Deal.mg offre un environnement sécurisé et une communauté active.
          Acheter de seconde main vous permet de trouver des vêtements de
          qualité à un prix bien inférieur au neuf.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          2. Comment assurer la sécurité lors de l'achat d'articles de mode ?
        </strong>
      ),
      answer: () => (
        <p>
          Demandez au vendeur des photos réelles de l'article porté ou sous
          différents angles via téléphone afin d'en vérifier l'usure. Pour les
          transactions à distance sur <strong>Deal.mg</strong>, privilégiez les
          envois par transporteurs reconnus et convenez clairement des modalités
          de paiement après preuve d'expédition. Une communication directe et
          transparente reste votre meilleure garantie.
        </p>
      ),
    },
    {
      question: () => (
        <strong>3. Comment vendre mes vêtements rapidement ?</strong>
      ),
      answer: () => (
        <p>
          Prenez des photos claires et rédigez une description précise de
          l'état, de la taille et de la marque de vos vêtements. L'utilisation
          de notre outil d'IA aide à générer une description optimisée pour le
          {" "}<strong>SEO mode.</strong>
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          4. Puis-je trouver des vêtements de marque en occasion ?
        </strong>
      ),
      answer: () => (
        <p>
          Oui, de nombreux utilisateurs revendent des vêtements de marque en
          excellent état. Utilisez la barre de recherche pour trouver vos
          marques préférées.
        </p>
      ),
    },
    {
      question: () => (
        <strong>5. Comment vérifier la taille et l'état d'un vêtement ?</strong>
      ),
      answer: () => (
        <p>
          Demandez les mesures précises (largeur d'épaule, longueur) et examinez
          les photos pour repérer d'éventuels défauts. Demandez si le vêtement a
          été lavé et s'il présente des signes d'usure.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          6. Comment fixer le juste prix pour mes vêtements à vendre ?
        </strong>
      ),
      answer: () => (
        <p>
          Comparez vos articles avec des modèles similaires publiés sur Deal.mg,
          en tenant compte de la marque, de l'état et de la demande.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          7. Existe-t-il des risques de contrefaçon pour les articles de mode ?
        </strong>
      ),
      answer: () => (
        <p>
          La vigilance est de mise. Pour les articles de marque (sacs,
          chaussures), demandez la facture d'origine, l'emballage ou vérifiez
          les détails de fabrication.
        </p>
      ),
    },
    {
      question: () => (
        <strong>8. Comment signaler un article de mode suspect ?</strong>
      ),
      answer: () => (
        <p>
          Si vous détectez une annonce frauduleuse ou un article contrefait,
          cliquez sur le bouton « Signaler » pour alerter notre équipe de
          modération.
        </p>
      ),
    },
    {
      question: () => (
        <strong>9. Puis-je vendre des vêtements pour enfants ?</strong>
      ),
      answer: () => (
        <p>
          Oui, la catégorie mode inclut une section dédiée aux vêtements,
          chaussures et accessoires pour enfants, très prisée par les parents
          malgaches.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          10. Comment vérifier l'état des vêtements et chaussures ?
        </strong>
      ),
      answer: () => (
        <p>
          Rencontrez toujours le vendeur en personne pour vérifier la qualité du
          tissu, essayer les chaussures et repérer d'éventuels défauts non
          mentionnés.
        </p>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Mode seconde main à Madagascar | Deal.mg</title>
        <meta
          name="description"
          content="Découvrez la mode seconde main à Madagascar sur Deal.mg : vêtements, chaussures et accessoires à prix réduits, consommation durable et vente facile."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: "Mode seconde main à Madagascar",
            image: ["https://deal.mg/logo.png"],
            description:
              "Vêtements, chaussures et accessoires de seconde main à Madagascar sur Deal.mg",
            sku: idCategorie,
            offers: {
              "@type": "Offer",
              url: `https://deal.mg/article/resultat?idCategorie=${idCategorie}`,
              availability: "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>

      <h1 className="text-center">
        Mode seconde main : style et économies à Madagascar
      </h1>

      <p>
        Renouvelez votre garde-robe avec la meilleure sélection de mode
        d'occasion à Madagascar. Trouvez des vêtements stylés, des chaussures et
        des accessoires de seconde main à prix réduits pour tous les styles.
        Adoptez une consommation durable en achetant sur notre plateforme
        sécurisée. Vendeurs, publiez vos articles facilement et attirez des
        acheteurs passionnés grâce à des descriptions optimisées par notre outil
        d'IA.
      </p>

      <h2>Vêtements pour femmes, hommes et enfants</h2>
      <p>
        Renouvelez votre garde-robe sans vous ruiner. Explorez notre vaste
        sélection de <strong>vêtements occasion</strong> : robes tendances,
        chemises classiques, pantalons, et tenues pour enfants.{" "}
        <strong>La mode à Madagascar</strong> se réinvente avec des pièces
        uniques et accessibles à tous.
      </p>

      <h2>Chaussures et accessoires</h2>
      <p>
        Complétez votre look avec des chaussures et accessoires de seconde main.
        Des baskets décontractées aux chaussures de soirée, en passant par les
        sacs à main, trouvez l'accessoire parfait pour chaque occasion.
      </p>

      <h2>Adoptez la mode éco-responsable</h2>
      <p>
        Faire du shopping sur Deal.mg, c'est opter pour une consommation
        durable. Donnez une seconde vie aux vêtements et contribuez à la
        réduction des déchets textiles. La mode éco-responsable à Madagascar est
        plus accessible que jamais. Il est aussi possible de trouver des{" "}
        <strong>articles de beauté</strong> ou des 
        {" "}
          <strong> objets de décoration </strong>
        pour parfaire votre style de vie.
      </p>

      <h2>Transformez votre garde-robe en cash</h2>
      <p>
        Vous avez des vêtements que vous ne portez plus ? Transformez-les en
        argent. Publiez une annonce détaillée en utilisant notre outil d'IA pour
        optimiser votre description et garantir une{" "}
        <strong>vente rapide</strong>.
      </p>

      <br />

      <h2 className="support_faq_title">
        <MessageSquare size={20} className="support_faq_title_icon" />
        FAQ
      </h2>

      {faqList.map((item, index) => (
        <div
          key={index}
          className={`support_faq_item ${openIndex === index ? "open" : ""}`}
        >
          <div
            className="support_faq_question"
            onClick={() => toggleFAQ(index)}
          >
            <span>{item.question()}</span>
            <ChevronDown
              className={`support_faq_arrow ${
                openIndex === index ? "support_faq_rotate_180" : ""
              }`}
              size={20}
            />
          </div>

          <div
            className={`support_faq_answer_container${
              openIndex === index ? "active" : ""
            }`}
          >
            <div className="support_faq_answer">{item.answer()}</div>
          </div>
        </div>
      ))}
    </>
  );
};

export default FaqMode;
